import { Sun } from "lucide-react";

interface UvGaugeProps {
  uvi: number;
}

function uvLabel(uvi: number): { label: string; color: string } {
  if (uvi <= 2) return { label: "Low", color: "oklch(0.7 0.18 145)" };
  if (uvi <= 5) return { label: "Moderate", color: "oklch(0.85 0.18 90)" };
  if (uvi <= 7) return { label: "High", color: "oklch(0.75 0.2 50)" };
  if (uvi <= 10) return { label: "Very High", color: "oklch(0.65 0.22 25)" };
  return { label: "Extreme", color: "oklch(0.55 0.25 320)" };
}

export function UvGauge({ uvi }: UvGaugeProps) {
  const max = 12;
  const progress = Math.max(0, Math.min(uvi / max, 1));
  const { label, color } = uvLabel(uvi);

  // Half circle gauge: 180deg arc from left to right going over the top
  const cx = 60;
  const cy = 55;
  const r = 40;

  // Start angle (left side) is π radians, sweep clockwise toward 0 (right side)
  // For SVG, screen Y is flipped, so y = cy - r*sin(angle)
  const startAngle = Math.PI;
  const endAngle = startAngle - progress * Math.PI;

  const startX = cx + r * Math.cos(startAngle);
  const startY = cy - r * Math.sin(startAngle);
  const endX = cx + r * Math.cos(endAngle);
  const endY = cy - r * Math.sin(endAngle);

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
        <Sun className="h-4 w-4 text-amber-400" />
        UV Index
      </h3>

      <div className="flex flex-col items-center">
        <svg viewBox="0 0 120 75" className="w-full max-w-[200px]">
          {/* Background arc - top half */}
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none"
            stroke="oklch(1 0 0 / 0.08)"
            strokeWidth="8"
            strokeLinecap="round"
          />

          {/* Filled progress arc */}
          {progress > 0 && (
            <path
              d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`}
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
            />
          )}

          {/* Value */}
          <text
            x={cx}
            y={cy - 5}
            textAnchor="middle"
            className="fill-foreground font-bold"
            fontSize="22"
          >
            {uvi.toFixed(1)}
          </text>
          <text
            x={cx}
            y={cy + 8}
            textAnchor="middle"
            className="fill-muted-foreground"
            fontSize="9"
          >
            of {max}
          </text>
        </svg>

        <div className="text-center -mt-1">
          <span className="text-sm font-semibold" style={{ color }}>
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}
